import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { getRandomAffirmation } from '@/data/affirmations';
import { API_BASE, publicFetch, setSession } from '@/lib/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [affirmation] = useState(getRandomAffirmation());

  useEffect(() => {
    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        setError('No session ID found. Please try logging in again.');
        return;
      }
      
      const sessionId = sessionIdMatch[1];
      
      try {
        const response = await publicFetch(`${API_BASE}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId })
        });
        
        if (!response.ok) {
          let detail = 'Authentication failed';
          try {
            const data = await response.json();
            detail = data.detail || detail;
          } catch {}
          setError(detail);
          return;
        }
        
        const data = await response.json();
        
        if (data.session_token) {
          setSession(data.session_token);
        }
        
        navigate('/file-room', { state: { user: data.user }, replace: true });
      } catch (err) {
        console.error('Auth error:', err);
        setError('Could not connect to the server. Please check your connection and try again.');
      }
    };
    
    processAuth();
  }, [location, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="bg-white p-8 max-w-md text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/file-room/login"
            className="inline-block bg-brand-dark px-6 py-2 text-white font-medium"
            data-testid="back-to-login-btn"
          >
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center">
      <div className="text-center max-w-md mx-4">
        <div className="animate-pulse mb-8">
          <Heart className="w-12 h-12 text-brand-gold mx-auto" />
        </div>
        <p className="text-white text-xl italic mb-4">"{affirmation}"</p>
        <p className="text-gray-400 text-sm">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
